import * as React from "react";
import {useEffect, useState} from "react";
import {AdminRegion, LoginContentAdminApi, ServicePill} from "../../api/LoginContentAdminApi";
import {prominentButtonStyle, raisedButtonClassOnlyStyle} from "../common/buttonStyles";
import {RaisedButton} from "../common/RaisedButton";
import {IToastNotification} from "../../utils/planUserRemoval";

export const ADMIN_LOGIN_PILL_CONTENT_DESCRIPTION =
    "These pills appear below the carousel. Each links to a page on aon.com.";
export const ADMIN_LOGIN_PILL_CONTENT_SAVE_BTN = "Save Pills";
export const ADMIN_LOGIN_PILL_CONTENT_ADD_BTN = "Add";
export const ADMIN_LOGIN_PILL_CONTENT_SAVED_SUCCESS = "Service pills saved";
export const ADMIN_LOGIN_PILL_CONTENT_SAVED_ERROR = "Failed to save pills";

interface AdminLoginServicePillContentProps {
    activeRegion: AdminRegion;
    setToast: (toast: IToastNotification) => void;
    onPillsSaved: (regionId: number) => void;
}

export const AdminLoginServicePillContent: React.FunctionComponent<AdminLoginServicePillContentProps> = (props) => {
    const [pills, setPills] = useState<ServicePill[]>(props.activeRegion.servicePills ?? []);
    const [newLabel, setNewLabel] = useState("");
    const [newUrl, setNewUrl] = useState("");
    const [isNewLabelInvalid, setIsNewLabelInvalid] = useState(false);
    const [isNewUrlInvalid, setIsNewUrlInvalid] = useState(false);
    const [invalidPillLabelIndexes, setInvalidPillLabelIndexes] = useState<number[]>([]);
    const [invalidPillUrlIndexes, setInvalidPillUrlIndexes] = useState<number[]>([]);
    const [dragIndex, setDragIndex] = useState<number | null>(null);

    useEffect(() => {
        setPills(props.activeRegion.servicePills ?? []);
        setNewLabel("");
        setNewUrl("");
        setIsNewLabelInvalid(false);
        setIsNewUrlInvalid(false);
        setInvalidPillLabelIndexes([]);
        setInvalidPillUrlIndexes([]);
    }, [props.activeRegion.id, props.activeRegion.servicePills]);

    const validatePills = (pillList: ServicePill[]) => {
        const labelIndexByValue = new Map<string, number[]>();
        const urlIndexByValue = new Map<string, number[]>();
        const invalidLabelIndexes = new Set<number>();
        const invalidUrlIndexes = new Set<number>();

        pillList.forEach((pill, index) => {
            const normalizedLabel = pill.label.trim().toLowerCase();
            if (!normalizedLabel) {
                invalidLabelIndexes.add(index);
            } else {
                labelIndexByValue.set(normalizedLabel, [...(labelIndexByValue.get(normalizedLabel) ?? []), index]);
            }

            const normalizedUrl = pill.url.trim().toLowerCase();
            if (!normalizedUrl) {
                invalidUrlIndexes.add(index);
            } else {
                urlIndexByValue.set(normalizedUrl, [...(urlIndexByValue.get(normalizedUrl) ?? []), index]);
            }
        });

        labelIndexByValue.forEach((indexes) => {
            if (indexes.length > 1) {
                indexes.forEach(index => invalidLabelIndexes.add(index));
            }
        });
        urlIndexByValue.forEach((indexes) => {
            if (indexes.length > 1) {
                indexes.forEach(index => invalidUrlIndexes.add(index));
            }
        });

        return {
            invalidLabelIndexes: Array.from(invalidLabelIndexes),
            invalidUrlIndexes: Array.from(invalidUrlIndexes),
        };
    };

    const hasTableValidationErrors = invalidPillLabelIndexes.length > 0 || invalidPillUrlIndexes.length > 0;

    const updatePill = (index: number, field: "label" | "url", value: string) => {
        setPills(prev => {
            const updatedPills = prev.map((pill, i) => i === index ? {...pill, [field]: value} : pill);
            if (hasTableValidationErrors) {
                const {invalidLabelIndexes, invalidUrlIndexes} = validatePills(updatedPills);
                setInvalidPillLabelIndexes(invalidLabelIndexes);
                setInvalidPillUrlIndexes(invalidUrlIndexes);
            }
            return updatedPills;
        });
    };

    const removePill = (index: number) => {
        setPills(prev => {
            const updatedPills = prev.filter((_, i) => i !== index);
            if (hasTableValidationErrors) {
                const {invalidLabelIndexes, invalidUrlIndexes} = validatePills(updatedPills);
                setInvalidPillLabelIndexes(invalidLabelIndexes);
                setInvalidPillUrlIndexes(invalidUrlIndexes);
            }
            return updatedPills;
        });
    };

    const addPill = () => {
        const trimmedLabel = newLabel.trim();
        const trimmedUrl = newUrl.trim();
        const labelMissing = !trimmedLabel;
        const urlMissing = !trimmedUrl;
        const normalizedLabel = trimmedLabel.toLowerCase();
        const normalizedUrl = trimmedUrl.toLowerCase();
        const labelExists = pills.some(pill => pill.label.trim().toLowerCase() === normalizedLabel);
        const urlExists = pills.some(pill => pill.url.trim().toLowerCase() === normalizedUrl);
        setIsNewLabelInvalid(labelMissing || labelExists);
        setIsNewUrlInvalid(urlMissing || urlExists);

        if (labelMissing || urlMissing || labelExists || urlExists) {
            return;
        }

        setPills(prev => [...prev, {label: trimmedLabel, url: trimmedUrl}]);
        setNewLabel("");
        setNewUrl("");
        setIsNewLabelInvalid(false);
        setIsNewUrlInvalid(false);
    };

    const handleAddKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Enter") {
            e.preventDefault();
            addPill();
        }
    };

    const handleDrop = (targetIndex: number) => {
        if (dragIndex === null || dragIndex === targetIndex) {
            setDragIndex(null);
            return;
        }
        setPills(prev => {
            const reordered = [...prev];
            const [moved] = reordered.splice(dragIndex, 1);
            reordered.splice(targetIndex, 0, moved);
            return reordered;
        });
        setDragIndex(null);
    };

    const savePillsHandler = async () => {
        const {invalidLabelIndexes, invalidUrlIndexes} = validatePills(pills);
        setInvalidPillLabelIndexes(invalidLabelIndexes);
        setInvalidPillUrlIndexes(invalidUrlIndexes);
        if (invalidLabelIndexes.length > 0 || invalidUrlIndexes.length > 0) {
            return;
        }

        LoginContentAdminApi.savePills(props.activeRegion.id, pills)
            .then(() => {
                props.setToast({message: ADMIN_LOGIN_PILL_CONTENT_SAVED_SUCCESS, type: "success"});
                props.onPillsSaved(props.activeRegion.id);
            }).catch(() => {
                props.setToast({message: ADMIN_LOGIN_PILL_CONTENT_SAVED_ERROR, type: "error"});
            });
    };

    return (
        <div className="admin-login-content__panel">
            <p className="admin-login-content__panel-description">
                {ADMIN_LOGIN_PILL_CONTENT_DESCRIPTION}
            </p>
            <table className="admin-login-content__pills-table">
                <thead>
                <tr>
                    <th className="admin-login-content__pills-table-grip" />
                    <th>Label</th>
                    <th>URL</th>
                    <th className="admin-login-content__pills-table-action" />
                </tr>
                </thead>
                <tbody>
                {pills.map((pill, index) => (
                    <tr
                        key={`pill-${index}`}
                        draggable={true}
                        data-testid={`admin-login-content__pill-row-${index}`}
                        onDragStart={() => setDragIndex(index)}
                        onDragOver={e => e.preventDefault()}
                        onDrop={() => handleDrop(index)}
                    >
                        <td className="admin-login-content__pills-table-grip">
                            <i className="fa-solid fa-grip-vertical" />
                        </td>
                        <td>
                            <input
                                type="text"
                                className={`admin-login-content__input admin-login-content__input--compact${invalidPillLabelIndexes.includes(index) ? " admin-login-content__input--invalid" : ""}`}
                                value={pill.label}
                                onChange={e => updatePill(index, "label", e.target.value)}
                            />
                        </td>
                        <td>
                            <input
                                type="text"
                                className={`admin-login-content__input admin-login-content__input--compact admin-login-content__input--url${invalidPillUrlIndexes.includes(index) ? " admin-login-content__input--invalid" : ""}`}
                                value={pill.url || ""}
                                onChange={e => updatePill(index, "url", e.target.value)}
                            />
                        </td>
                        <td className="admin-login-content__pills-table-action">
                            <RaisedButton
                                type="button"
                                className="admin-login-content__pill-remove"
                                style={raisedButtonClassOnlyStyle}
                                primary={false}
                                onClick={() => removePill(index)}
                            >
                                <i className="fa-solid fa-times" />
                            </RaisedButton>
                        </td>
                    </tr>
                ))}
                </tbody>
            </table>
            <div className="admin-login-content__add-pill-row">
                <input
                    type="text"
                    className={`admin-login-content__input admin-login-content__input--compact${isNewLabelInvalid ? " admin-login-content__input--invalid" : ""}`}
                    placeholder="New pill label"
                    value={newLabel}
                    onChange={e => {
                        const nextValue = e.target.value;
                        setNewLabel(nextValue);
                        if (nextValue.trim()) {
                            setIsNewLabelInvalid(false);
                        }
                    }}
                    onKeyDown={handleAddKeyDown}
                />
                <input
                    type="text"
                    className={`admin-login-content__input admin-login-content__input--compact${isNewUrlInvalid ? " admin-login-content__input--invalid" : ""}`}
                    placeholder="https://www.aon.com/..."
                    value={newUrl}
                    onChange={e => {
                        const nextValue = e.target.value;
                        setNewUrl(nextValue);
                        if (nextValue.trim()) {
                            setIsNewUrlInvalid(false);
                        }
                    }}
                    onKeyDown={handleAddKeyDown}
                />
                <RaisedButton
                    type="button"
                    className="admin-login-content__btn"
                    style={raisedButtonClassOnlyStyle}
                    primary={false}
                    onClick={addPill}
                >
                    <i className="fa-solid fa-plus" /> {ADMIN_LOGIN_PILL_CONTENT_ADD_BTN}
                </RaisedButton>
            </div>
            <span className="admin-login-content__form-hint">
                Each pill links to the relevant page on aon.com. Opens in a new tab.
            </span>
            <div className="admin-login-content__actions">
                <RaisedButton
                    type="button"
                    className="admin-login-content__btn admin-login-content__btn--primary"
                    style={prominentButtonStyle}
                    primary={true}
                    onClick={() => void savePillsHandler()}
                >
                    <i className="fa-solid fa-check" /> {ADMIN_LOGIN_PILL_CONTENT_SAVE_BTN}
                </RaisedButton>
            </div>
        </div>
    );
};
